import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PoweredByMeitre } from '@shared/components/common';
import {
  Calendar,
  UtensilsCrossed,
  MessageCircle,
  Instagram,
  UserRound,
  Gift,
  Loader2,
} from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/components/ui/avatar";
import { useTenant } from "@core/tenant";
import { useAuth } from "@core/auth";
import { useLanguage } from "@core/i18n";
import { LanguageSelector } from "@shared/components/common";
import BannerSection from "@features/landing/components/BannerSection";
import EventRecommendations from "@features/landing/components/EventRecommendations";
import { useBooking } from "@features/booking/state";
import { fetchSpecialEventGroup } from "@features/booking/api/specialEvents";
import type { ServiceType } from "@features/booking/state/types";
import ExperienceCard from "@features/booking/components/ExperienceCard";
// import ReviewsHighlight from "@features/booking/components/ReviewsHighlight";
import type { RestaurantMenu } from "@shared/types/restaurant";
import { menuPriceLabel } from "@shared/utils/menuPrice";

const Index = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { restaurant, subdomainPrefix } = useTenant();
  const { authState, logout, login } = useAuth();
  const { t: tBooking } = useTranslation('booking');
  const { t: tCommon } = useTranslation('common');
  const { currentLanguage } = useLanguage();
  const { updateBooking, setSelectedDate, setService, resetBooking } = useBooking();
  const [eventLoading, setEventLoading] = useState(false);

  const recommendations = restaurant?.eventRecommendations?.[currentLanguage] ?? [];

  // Filter and sort menus for display on home page
  const displayMenus = React.useMemo(() => {
    const langMenus = restaurant?.menus?.[currentLanguage];
    if (!langMenus) return [];

    // menus can be a single object or array-like indexed by string keys
    const menuList: RestaurantMenu[] = Array.isArray(langMenus)
      ? langMenus
      : Object.values(langMenus);

    return menuList
      .filter((m) =>
        m.showInMainView &&
        !m.eventMenu &&
        !m.privateMenu &&
        !m.marketplace &&
        !m.isDeleted &&
        !m.preferredAccess
      )
      .sort((a, b) => (a.orderPriority ?? 999) - (b.orderPriority ?? 999));
  }, [restaurant, currentLanguage]);

  const handleMenuSelect = (menuId: number) => {
    resetBooking();
    const menu = displayMenus.find((m) => m.id === menuId);
    const minDiners = menu?.minPartySizeAllowed || restaurant.minPartySizeAllowed || 1;
    updateBooking({ preSelectedMenuId: menuId, selectedMenuFilter: [menuId], diners: minDiners + 1 });
    navigate('/select-diners');
  };

  const isLoggedIn = authState.isAuthenticated;
  const userEmail = authState.email || '';
  const userName = authState.name || userEmail.split('@')[0] || '';

  // No mount-time resetBooking — the previous behavior wiped in-progress
  // bookings whenever the user navigated to /, including via browser
  // back-button or URL bar typing. Users routinely return to / mid-flow
  // (tap logo to check the menu, swipe-back gesture, etc.) and expect
  // their booking to resume. The explicit "Book a table" / "View menu"
  // handlers below still call resetBooking() at click time, so any user
  // intentionally starting a new booking still gets a clean state.
  // Root cause of Sentry MEITRE-FRONTEND-V2-BF Pattern D (~125 users/day
  // before this fix, separate from Pattern A which is iOS tab eviction).

  // Detect ?eventSlug= and load special event
  useEffect(() => {
    const eventSlug = searchParams.get('eventSlug');
    if (!eventSlug || !subdomainPrefix) return;

    // Reset before overlaying the event so abandoned prior-flow state
    // (preSelectedMenuId, cardToken, guaranteeType, diners, etc.) doesn't
    // leak across event contexts. Previously the mount-time resetBooking
    // useEffect above made this implicit — pre-#254 review caught the
    // regression here. cardToken + guaranteeType crossing event fee tiers
    // is the most painful subset (payment-adjacent).
    resetBooking();

    // Clean URL immediately
    const url = new URL(window.location.href);
    url.searchParams.delete('eventSlug');
    const overrideDate = url.searchParams.get('date');
    const overrideServices = url.searchParams.get('serviceTypes') || url.searchParams.get('serviceType');
    url.searchParams.delete('date');
    url.searchParams.delete('serviceTypes');
    url.searchParams.delete('serviceType');
    window.history.replaceState({}, '', url.toString());

    setEventLoading(true);

    fetchSpecialEventGroup(subdomainPrefix, eventSlug)
      .then((event) => {
        // Apply URL overrides if provided
        if (overrideDate) {
          event.dates = [overrideDate.split('T')[0]];
        }
        if (overrideServices) {
          event.serviceTypes = overrideServices.split(',');
        }

        updateBooking({ specialEvent: event });

        // Auto-select date if single
        if (event.dates.length === 1) {
          setSelectedDate(event.dates[0]);
        }

        // Auto-select service if single
        if (event.serviceTypes.length === 1) {
          setService(event.serviceTypes[0] as ServiceType);
        }

        navigate('/select-diners');
      })
      .catch((err) => {
        console.error('[EventSlug] Failed to load event:', err);
      })
      .finally(() => {
        setEventLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookTable = () => {
    resetBooking();
    navigate("/select-diners");
  };

  // -- Hardcoded data commented out until backend support is available --
  // const upcomingEvents = [
  //   { id: 1, title: "Wine Tasting Experience", date: "Apr 16", time: "19:00", description: "Discover our curated selection of local wines" },
  //   { id: 2, title: "Chef's Special Dinner", date: "Apr 23", time: "20:00", description: "A five-course tasting menu showcasing seasonal ingredients" },
  // ];
  //
  // const experiences = [
  //   { id: 1, title: "Full Premium Wine Tasting at Sunset", description: "...", image: "...", price: "ARS 12,500" },
  //   { id: 2, title: "Guided Tour with Abbreviated Pairing", description: "...", image: "...", price: "ARS 9,800" },
  //   { id: 3, title: "Private Chef's Table Experience", description: "...", image: "...", price: "ARS 15,000" },
  // ];

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center p-0 lg:p-4">
      <div className="max-w-2xl w-full bg-card shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl overflow-visible lg:overflow-hidden">
      <header className="booking-header px-6 sticky top-0 z-20 bg-card lg:translucent:bg-transparent max-lg:translucent:backdrop-blur-sm">
        <div className="w-10 flex justify-start">
          <LanguageSelector size="sm" />
        </div>

        {restaurant?.website ? (
          <a href={restaurant.website} target="_blank" rel="noopener noreferrer">
            {restaurant?.customStyle?.headerImage ? (
              <img
                src={restaurant.customStyle.headerImage}
                alt={restaurant.name ?? 'Restaurant'}
                className="h-10 max-w-[180px] object-contain"
              />
            ) : (
              <h1 className="text-xl sm:text-2xl font-display tracking-wide">{restaurant?.name ?? 'Restaurant'}</h1>
            )}
          </a>
        ) : restaurant?.customStyle?.headerImage ? (
          <img
            src={restaurant.customStyle.headerImage}
            alt={restaurant.name ?? 'Restaurant'}
            className="h-10 max-w-[180px] object-contain"
          />
        ) : (
          <h1 className="text-xl sm:text-2xl font-display tracking-wide">{restaurant?.name ?? 'Restaurant'}</h1>
        )}

        <div className="w-10 flex justify-end">
        {isLoggedIn ? (
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full hover:bg-brand-muted/20"
            onClick={() => navigate('/profile')}
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src="" />
              <AvatarFallback className="bg-brand-muted text-brand">
                {authState.name
                  ? authState.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                  : userName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full hover:bg-brand-muted/20"
            onClick={() => login()}
          >
            <UserRound className="h-5 w-5 text-foreground/70" />
          </Button>
        )}
        </div>
      </header>

      <main className="flex-1 flex flex-col px-6 pt-8 pb-12">
        <div className="mb-8 text-center">
          {isLoggedIn ? (
            <>
              <h2 className="text-3xl font-display mb-2">{tBooking('NEW_HOME_WELCOME_BACK')}</h2>
              <p className="text-xl text-foreground/70 font-medium">{userName}</p>
              <button
                className="mt-2 text-sm text-brand font-medium uppercase tracking-wide"
                onClick={() => { logout(); login(); }}
              >
                {tBooking('NEW_HOME_CHANGE_USER')}
              </button>
            </>
          ) : (
            <h2 className="text-3xl font-display mb-2">{tBooking('NEW_HOME_WELCOME')}</h2>
          )}
        </div>

        <div className="space-y-5 mb-10">
          <div className="relative">
            <Button
              data-testid="reserve-table-btn"
              disabled={restaurant?.blockBookATable}
              className="w-full py-6 text-lg gap-2 bg-brand hover:bg-brand-dark text-brand-foreground rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
              onClick={handleBookTable}
            >
              <Calendar size={20} />
              {tBooking('NEW_HOME_BOOK')}
            </Button>
            {restaurant?.blockBookATable && (
              <span
                aria-hidden="true"
                className="absolute -top-2 right-3 rounded-full bg-brand-dark text-brand-foreground text-xs font-semibold uppercase tracking-wide px-6 py-1.5 shadow-md pointer-events-none whitespace-nowrap"
              >
                {tBooking('NOT_AVAILABLE')}
              </span>
            )}
          </div>

          {restaurant?.menuURL && (
            <Button
              variant="outline"
              className="w-full py-5 text-base gap-2 bg-transparent border-brand text-brand hover:bg-brand-muted rounded-xl flex items-center justify-center"
              onClick={() => window.open(restaurant.menuURL!, "_blank")}
            >
              <UtensilsCrossed size={18} />
              {tBooking('NEW_HOME_VIEW_MENU')}
            </Button>
          )}

          {restaurant?.allowGiftCards && (
            <Button
              data-testid="buy-gift-card-btn"
              variant="outline"
              className="w-full py-5 text-base gap-2 bg-transparent border-brand text-brand hover:bg-brand-muted rounded-xl flex items-center justify-center"
              onClick={() => navigate('/gift-card')}
            >
              <Gift size={18} />
              {tBooking('NEW_HOME_BUY_GIFTCARD')}
            </Button>
          )}

          {/* {restaurant?.whatsAppEnabled && restaurant?.whatsAppPhoneNumber && (
            <Button
              variant="outline"
              className="w-full py-5 text-base gap-2 bg-transparent border-brand text-brand hover:bg-brand-muted rounded-xl flex items-center justify-center"
              onClick={() => window.open(`https://wa.me/${restaurant.whatsAppPhoneNumber.replace(/[^0-9]/g, '')}`, "_blank")}
            >
              <MessageCircle size={18} />
              {tCommon('FOOTER_WHATSAPP_HELP')}
            </Button>
          )} */}

          {restaurant?.instagramAccount && (
            <Button
              variant="outline"
              className="w-full py-5 text-base gap-2 bg-transparent border-brand text-brand hover:bg-brand-muted rounded-xl"
              onClick={() => {
                const ig = restaurant.instagramAccount!;
                const url = ig.startsWith('http')
                  ? ig
                  : `https://instagram.com/${ig.replace(/^@/, '')}`;
                window.open(url, '_blank');
              }}
            >
              <Instagram size={18} />
              {tCommon('FOOTER_INSTAGRAM')}
            </Button>
          )}
        </div>

      </main>

      {displayMenus.length > 0 && (
        <div className="px-6 pb-8">
          <h3 className="text-2xl font-display text-foreground mb-4 text-left">
            {tBooking('NEW_HOME_EXPERIENCES')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayMenus.map((menu) => (
              <ExperienceCard
                key={menu.id}
                experience={{
                  id: menu.id,
                  title: menu.name,
                  description: menu.description || '',
                  details: menu.details,
                  image: menu.image,
                  price: menuPriceLabel(menu) ?? undefined,
                  buttonLabel: tBooking('CALL_TO_ACTION_1'),
                  detailLabel: tBooking('NEW_HOME_VIEW_MORE'),
                  onBook: () => handleMenuSelect(menu.id),
                }}
              />
            ))}
          </div>
        </div>
      )}

      <footer className="py-6 text-center bg-foreground/5">
        <div className="text-xs">
          <PoweredByMeitre />
        </div>
      </footer>
      </div>

      {(recommendations.length > 0 || (restaurant?.allowBanner && restaurant?.bannerImage)) && (() => {
        const hasBoth = recommendations.length > 0 && restaurant?.allowBanner && restaurant?.bannerImage;
        return (
          <div className={`w-full mt-6 ${hasBoth ? 'max-w-2xl' : 'max-w-[375px] mx-auto'}`}>
            <div className={`grid gap-6 ${
              hasBoth ? 'grid-cols-1 sm:grid-cols-2 sm:items-start' : 'grid-cols-1'
            }`}>
              {recommendations.length > 0 && (
                <div>
                  <h3 className={`text-lg font-bold text-white mb-5 [text-shadow:_0_1px_3px_rgba(0,0,0,0.6)] ${hasBoth ? 'text-center sm:text-left' : 'text-center'}`}>
                    {tBooking('NEW_HOME_RECOMMENDED')}
                  </h3>
                  <EventRecommendations recommendations={recommendations} />
                </div>
              )}
              {restaurant?.allowBanner && restaurant?.bannerImage && (
                <div className={recommendations.length > 0 ? 'sm:pt-[48px]' : ''}>
                  <BannerSection
                    bannerImage={restaurant.bannerImage}
                    bannerURL={restaurant.bannerURL || undefined}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Index;
